/**
 * Line Geometry Helpers
 *
 * Utility functions for line geometry operations.
 * Provides shared logic to avoid code duplication (DRY principle).
 *
 * @module utils/geometry/lineHelpers
 */

import type { Coordinates } from '../../types';
import { MIN_UNIQUE_POINTS_FOR_LINE } from '../../constants/geometry';

/**
 * Calculate the center point of a line (average of all points)
 *
 * Used by:
 * - useLineDrag (whole line drag)
 * - useLinePointDrag (individual point drag)
 * - Any component that needs line center calculation
 *
 * @param points - Array of line points
 * @returns Center coordinates (average lat/lng) or null if no points
 *
 * @example
 * ```typescript
 * const center = calculateLineCenter([
 *   { lat: 52.0, lng: 20.0 },
 *   { lat: 52.5, lng: 20.5 }
 * ]);
 * // Returns: { lat: 52.25, lng: 20.25 }
 * ```
 */
export function calculateLineCenter(points: Coordinates[]): Coordinates | null {
  if (!points || points.length === 0) {
    return null;
  }

  const avgLat = points.reduce((sum: number, p: Coordinates) => sum + p.lat, 0) / points.length;
  const avgLng = points.reduce((sum: number, p: Coordinates) => sum + p.lng, 0) / points.length;

  return { lat: avgLat, lng: avgLng };
}

/**
 * Get count of unique points in line (excluding duplicates from connections)
 *
 * When connecting back to existing points (e.g., 1→2→3→1), linePoints array
 * contains duplicates of those points. This function counts only UNIQUE coordinates.
 *
 * Used by:
 * - DrawingInstructions (tooltip count display)
 * - LinePanels (point count display)
 *
 * @param linePoints - Array of line points (may contain duplicates from connections)
 * @returns Count of unique coordinate pairs
 *
 * @example
 * ```typescript
 * // Line 1→2→3→1 creates [p1, p2, p3, p1_duplicate]
 * getUniquePointCount([p1, p2, p3, p1_dup]); // → 3 (not 4)
 * ```
 *
 * @example
 * ```typescript
 * // Line with branching: 1→2→3→1, branch from 2 to 4,5
 * getUniquePointCount([p1, p2, p3, p1_dup, p4, p5]); // → 5 (not 6)
 * ```
 *
 * @since 3.1.0 - Added for accurate point count display in tooltips
 */
export function getUniquePointCount(linePoints: Coordinates[]): number {
  return linePoints.filter((point, index, arr) =>
    // Keep only FIRST occurrence of each unique coordinate
    arr.findIndex(
      (p) => p.lat === point.lat && p.lng === point.lng
    ) === index
  ).length;
}

/**
 * Calculate the appropriate active parent index for a line geometry
 *
 * Determines which point should be highlighted as "active" (green) to indicate
 * where the next point will connect. Handles both regular lines and closed shapes.
 *
 * Used by:
 * - drawingToolsSlice (setActiveGeometry, undo, redo)
 * - Ensures consistent active vertex calculation across all code paths
 *
 * Logic:
 * - Empty line → null
 * - Closed shape (last point duplicates earlier point) → original point index
 * - Regular line → last point index
 *
 * @param linePoints - Array of line points
 * @returns Index of the active parent point, or null if no points
 *
 * @example Regular line (unique points)
 * ```typescript
 * // Line 1→2→3
 * const points = [
 *   { lat: 50.0, lng: 20.0 }, // point 1
 *   { lat: 50.1, lng: 20.1 }, // point 2
 *   { lat: 50.2, lng: 20.2 }  // point 3
 * ];
 * calculateActiveParentIndex(points); // → 2 (last point)
 * ```
 *
 * @example Closed shape (duplicate detection)
 * ```typescript
 * // Triangle 1→2→3→1
 * const points = [
 *   { lat: 50.0, lng: 20.0 }, // point 1 (original)
 *   { lat: 50.1, lng: 20.1 }, // point 2
 *   { lat: 50.2, lng: 20.2 }, // point 3
 *   { lat: 50.0, lng: 20.0 }  // point 1 (duplicate)
 * ];
 * calculateActiveParentIndex(points); // → 0 (original point 1)
 * ```
 *
 * @since 3.1.2 - Added to fix undo/redo active vertex bug
 */
export function calculateActiveParentIndex(linePoints: Coordinates[]): number | null {
  // Empty line → no active parent
  if (!linePoints || linePoints.length === 0) {
    return null;
  }

  const lastIndex = linePoints.length - 1;
  const lastPoint = linePoints[lastIndex];

  if (!lastPoint) {
    // No last point (shouldn't happen, but handle gracefully)
    return lastIndex > 0 ? lastIndex : 0;
  }

  // Find if last point is duplicate of any earlier point (for closed shapes)
  // Example: Triangle 1→2→3→1, last point is duplicate of point 1 (index 0)
  const originalIndex = linePoints.findIndex((p, idx) =>
    idx < lastIndex && // Only check earlier points
    p.lat === lastPoint.lat &&
    p.lng === lastPoint.lng
  );

  if (originalIndex !== -1) {
    // Last point is duplicate → use original point as active (green highlight)
    // Example: Triangle 1→2→3→1 → activeParentIndex = 0 (point 1)
    return originalIndex;
  }

  // Last point is unique → use it as active
  // Example: Line 1→2→3 → activeParentIndex = 2 (point 3)
  return lastIndex;
}

// ===== VERTEX DELETION HELPERS =====

/**
 * Find all indices with same coordinates as target point
 *
 * When a line has connections (e.g., 1→2→3→1), linePoints contains
 * duplicates at the same coordinates. This function finds ALL indices
 * at the target coordinate to ensure synchronized deletion.
 *
 * @internal
 * @param targetIndex - Index of the point to find duplicates for
 * @param linePoints - Array of line points
 * @returns Array of all indices with same coordinates as target
 *
 * @example
 * ```typescript
 * // Line 1→2→3→1: linePoints = [p1, p2, p3, p1_dup]
 * findDuplicateIndices(0, linePoints); // → [0, 3] (both instances of p1)
 * ```
 */
function findDuplicateIndices(
  targetIndex: number,
  linePoints: Coordinates[]
): number[] {
  const targetCoord = linePoints[targetIndex];
  if (!targetCoord) return [targetIndex];

  return linePoints
    .map((point, idx) =>
      point.lat === targetCoord.lat && point.lng === targetCoord.lng ? idx : null
    )
    .filter((idx): idx is number => idx !== null);
}

/**
 * Find all DIRECT children (not descendants) of a point
 *
 * Unlike findDescendants which recursively finds all descendants,
 * this only finds immediate children at depth 1 (no grandchildren).
 * This is used for child reconnection strategy instead of cascade deletion.
 *
 * @internal
 * @param parentIdx - Index of the parent point
 * @param parentIndices - Array of parent indices (parent-child relationships)
 * @returns Array of direct child indices
 *
 * @example
 * ```typescript
 * // Line: 1→2→3, branch from 2 to 4→5
 * // parentIndices: [null, 0, 1, 1, 3]
 * //                 p1    p2  p3  p4  p5
 * findDirectChildren(1, parentIndices); // → [2, 3] (p3 and p4 are children of p2)
 * // Does NOT include p5 (grandchild of p2)
 * ```
 */
function findDirectChildren(
  parentIdx: number,
  parentIndices: (number | null)[]
): number[] {
  return parentIndices
    .map((parent, childIdx) => (parent === parentIdx ? childIdx : null))
    .filter((idx): idx is number => idx !== null);
}

/**
 * Rebuild arrays with index remapping after removing specified indices
 *
 * After deleting points, indices shift. This function:
 * 1. Removes points at specified indices
 * 2. Creates mapping from old indices to new indices
 * 3. Updates parentIndices to use new mapped indices
 *
 * @internal
 * @param linePoints - Original array of line points
 * @param parentIndices - Original array of parent indices
 * @param indicesToRemove - Set of indices to remove
 * @returns New arrays with updated indices
 *
 * @example
 * ```typescript
 * // Remove index 1 from [p1, p2, p3, p4, p5]
 * // Old indices:     [0,  1,  2,  3,  4]
 * // New indices:     [0,      1,  2,  3]
 * // parentIndices: [null, 0, 1, 1, 3] → [null, 0, 0, 2]
 * ```
 */
function rebuildArraysWithRemapping(
  linePoints: Coordinates[],
  parentIndices: (number | null)[],
  indicesToRemove: Set<number>
): { linePoints: Coordinates[]; parentIndices: (number | null)[] } {
  const newPoints: Coordinates[] = [];
  const newParentIndices: (number | null)[] = [];
  const oldToNewIndexMap = new Map<number, number>();

  // Build new arrays and index mapping
  let newIndex = 0;
  linePoints.forEach((point, oldIndex) => {
    if (!indicesToRemove.has(oldIndex)) {
      newPoints.push(point);
      oldToNewIndexMap.set(oldIndex, newIndex);
      newIndex++;
    }
  });

  // Rebuild parentIndices with remapped indices
  linePoints.forEach((_, oldIndex) => {
    if (!indicesToRemove.has(oldIndex)) {
      const oldParent = parentIndices[oldIndex];

      if (oldParent === null || oldParent === undefined) {
        // Root remains root (or undefined parent → treat as root)
        newParentIndices.push(null);
      } else if (typeof oldParent === 'number') {
        // Parent exists → use new index (or null if parent was deleted)
        newParentIndices.push(oldToNewIndexMap.get(oldParent) ?? null);
      } else {
        // Unexpected type → make this a new root (safety fallback)
        newParentIndices.push(null);
      }
    }
  });

  return { linePoints: newPoints, parentIndices: newParentIndices };
}

/**
 * Delete vertex with child reconnection for branched lines
 *
 * Deletion strategy (NON-CASCADE):
 * - Leaf nodes: Simple removal
 * - Intermediate nodes: Reconnect children to grandparent
 * - Points with duplicates: Delete ALL instances
 * - Root nodes: Promote first child to root
 *
 * Process:
 * 1. Find all duplicates at same coordinates
 * 2. Find all DIRECT children (not descendants)
 * 3. Reconnect children to grandparent
 * 4. Remove only the deleted point (not descendants!)
 * 5. Rebuild parentIndices with index remapping
 *
 * Returns `null` if fewer than MIN_UNIQUE_POINTS_FOR_LINE remain.
 *
 * @param selectedIndex - Index of vertex to delete
 * @param linePoints - Array of line points
 * @param parentIndices - Array of parent indices (optional for backward compatibility)
 * @returns Updated arrays or null to delete entire line
 *
 * @example Delete intermediate node (child reconnection)
 * ```typescript
 * // Line: 1→2→3→4
 * // linePoints: [p1, p2, p3, p4]
 * // parentIndices: [null, 0, 1, 2]
 * //
 * // Delete point 2 (index 1):
 * deleteVertexWithCascade(1, linePoints, parentIndices);
 * // → { linePoints: [p1, p3, p4], parentIndices: [null, 0, 1] }
 * // p3 reconnected to p1 (grandparent) ✅
 * ```
 *
 * @example Delete branched point (reconnect all children)
 * ```typescript
 * // Line: 1→2→3 + branch 2→5→6
 * // linePoints: [p1, p2, p3, p5, p6]
 * // parentIndices: [null, 0, 1, 1, 3]
 * //
 * // Delete point 2 (index 1, has 2 children: p3, p5):
 * deleteVertexWithCascade(1, linePoints, parentIndices);
 * // → { linePoints: [p1, p3, p5, p6], parentIndices: [null, 0, 0, 2] }
 * // Both p3 and p5 reconnected to p1 ✅
 * ```
 *
 * @example Delete point in loop (duplicate handling)
 * ```typescript
 * // Triangle: 1→2→3→1
 * // linePoints: [p1, p2, p3, p1_dup]
 * // parentIndices: [null, 0, 1, 2]
 * //
 * // Delete point 1 (index 0, has duplicate at index 3):
 * deleteVertexWithCascade(0, linePoints, parentIndices);
 * // → { linePoints: [p2, p3], parentIndices: [null, 0] }
 * // Both p1 instances removed, p2 promoted to root ✅
 * ```
 *
 * @since 3.1.0 - Added for proper vertex deletion in Selection mode
 * @since 3.1.1 - Changed from cascade deletion to child reconnection strategy
 */
export function deleteVertexWithCascade(
  selectedIndex: number,
  linePoints: Coordinates[],
  parentIndices: (number | null)[] = []
): { linePoints: Coordinates[]; parentIndices: (number | null)[] } | null {
  // Validation
  if (selectedIndex < 0 || selectedIndex >= linePoints.length) {
    console.warn(
      `deleteVertexWithCascade: Invalid index ${selectedIndex} (array length: ${linePoints.length})`
    );
    return null;
  }

  // If no parentIndices (old sequential lines), fallback to simple deletion
  if (parentIndices.length === 0) {
    const newPoints = linePoints.filter((_, idx) => idx !== selectedIndex);
    const uniqueCount = getUniquePointCount(newPoints);

    return uniqueCount >= MIN_UNIQUE_POINTS_FOR_LINE
      ? { linePoints: newPoints, parentIndices: [] }
      : null;
  }

  // STEP 1: Find all indices at same coordinates (duplicates)
  const duplicateIndices = findDuplicateIndices(selectedIndex, linePoints);

  // STEP 2: Find all DIRECT children of each duplicate (not descendants!)
  const allDirectChildren = new Set<number>();
  duplicateIndices.forEach((idx) => {
    const children = findDirectChildren(idx, parentIndices);
    children.forEach((c) => allDirectChildren.add(c));
  });

  // STEP 3: Find grandparent (parent of deleted point)
  const grandparentIndex = parentIndices[selectedIndex];

  // STEP 4: Reconnect children to grandparent (KEY FIX - child reconnection!)
  const newParentIndices = [...parentIndices];
  allDirectChildren.forEach((childIdx) => {
    if (grandparentIndex !== null && grandparentIndex !== undefined) {
      // Normal case: reconnect to grandparent
      newParentIndices[childIdx] = grandparentIndex;
    } else {
      // Deleting root: promote first child to root
      const firstChild = Math.min(...Array.from(allDirectChildren));
      if (childIdx === firstChild) {
        newParentIndices[childIdx] = null; // First child becomes new root
      } else {
        newParentIndices[childIdx] = firstChild; // Other children connect to new root
      }
    }
  });

  // STEP 5: Remove only duplicate points (not descendants!)
  const indicesToRemove = new Set(duplicateIndices);

  // STEP 6: Check if line should be deleted entirely
  const remainingPoints = linePoints.filter((_, idx) => !indicesToRemove.has(idx));
  const uniqueRemaining = getUniquePointCount(remainingPoints);

  if (uniqueRemaining < MIN_UNIQUE_POINTS_FOR_LINE) {
    // Not enough unique points for a line → delete entire geometry
    return null;
  }

  // STEP 7: Rebuild arrays with index remapping (use newParentIndices with reconnections!)
  return rebuildArraysWithRemapping(linePoints, newParentIndices, indicesToRemove);
}
